import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Row, Col, Card, Button, ListGroup, FormControl, InputGroup } from 'react-bootstrap';
import { FaUserInjured, FaUserMd, FaPills, FaFileMedical, FaSearch } from 'react-icons/fa';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const Dashboard = () => {
  const [stats, setStats] = useState({ patients: 0, doctors: 0, medicines: 0, reports: 0 });
  const [recentPatients, setRecentPatients] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const patientsRes = await axios.get('/api/patients.php');
        const doctorsRes = await axios.get('/api/doctors.php');
        const medicinesRes = await axios.get('/api/medicines.php');
        const reportsRes = await axios.get('/api/reports.php');
        const medicinesCount = medicinesRes.data.pagination?.totalItems || medicinesRes.data.medicines?.length || 0;
        setStats({ patients: patientsRes.data.length, doctors: doctorsRes.data.length, medicines: medicinesCount, reports: reportsRes.data.length });
        setRecentPatients(patientsRes.data.slice(0, 5));
        setRecentReports(reportsRes.data.slice(0, 5));
      } catch (err) {
        console.error(err);
      }
    };
    fetchDashboardData();
  }, []);
  
  useEffect(() => {
    if (!debouncedSearchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const searchPatients = async () => {
      try {
        const res = await axios.get(`/api/patients/search/${debouncedSearchQuery}`);
        setSearchResults(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    searchPatients();
  }, [debouncedSearchQuery]);
  
  return (
    <div>
      <h2 className="mb-4">Dashboard</h2>
      <Row>
        <Col md={3}>
          <Card className="text-center mb-4 bg-primary text-white">
            <Card.Body>
              <FaUserInjured size={30} />
              <h3 className="mt-2">{stats.patients}</h3>
              <Card.Text>Patients</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center mb-4 bg-success text-white">
            <Card.Body>
              <FaUserMd size={30} />
              <h3 className="mt-2">{stats.doctors}</h3>
              <Card.Text>Doctors</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center mb-4 bg-warning text-white">
            <Card.Body>
              <FaPills size={30} />
              <h3 className="mt-2">{stats.medicines}</h3>
              <Card.Text>Medicines</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center mb-4 bg-info text-white">
            <Card.Body>
              <FaFileMedical size={30} />
              <h3 className="mt-2">{stats.reports}</h3>
              <Card.Text>Reports</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Card className="mb-4">
        <Card.Header><h5 className="mb-0">Patient Search</h5></Card.Header>
        <Card.Body>
          <InputGroup className="mb-3">
            <FormControl placeholder="Search by patient name or ID" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            <Button variant="primary" disabled><FaSearch /> Search</Button>
          </InputGroup>
          {searchResults.length > 0 && (
            <div className="search-results">
              <h6>Search Results:</h6>
              <ListGroup>
                {searchResults.map(patient => (
                  <ListGroup.Item key={patient.id}>
                    <Link to={`/patients/${patient.id}`}>{patient.name} - <span className="patient-id">{patient.patientId}</span></Link>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}
        </Card.Body>
      </Card>
      <Row>
        <Col md={6}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Recent Patients</h5>
              <Link to="/patients"><Button variant="outline-primary" size="sm">View All</Button></Link>
            </Card.Header>
            <ListGroup variant="flush">
              {recentPatients.length > 0 ? recentPatients.map(patient => (
                <ListGroup.Item key={patient.id}>
                  <Link to={`/patients/${patient.id}`} className="text-decoration-none">{patient.name} - <span className="patient-id">{patient.patientId}</span></Link>
                  <div className="text-muted small">Age: {patient.age} | Gender: {patient.gender}</div>
                </ListGroup.Item>
              )) : <ListGroup.Item>No patients found.</ListGroup.Item>}
            </ListGroup>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Recent Reports</h5>
              <Link to="/reports"><Button variant="outline-primary" size="sm">View All</Button></Link>
            </Card.Header>
            <ListGroup variant="flush">
              {recentReports.length > 0 ? recentReports.map(report => (
                <ListGroup.Item key={report.id}>
                  <Link to={`/reports/${report.id}`} className="text-decoration-none">
                    <span className="report-id">{report.reportId || 'N/A'}</span> - {report.patient?.name || 'N/A'}
                  </Link>
                  <div className="text-muted small">Doctor: {report.doctor?.name || 'N/A'} | Date: {report.date ? new Date(report.date).toLocaleDateString() : 'N/A'}</div>
                </ListGroup.Item>
              )) : <ListGroup.Item>No reports found.</ListGroup.Item>}
            </ListGroup>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
